import { useId } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import { FieldShell } from "./FieldShell";
import {
  toOptions,
  useFieldControl,
  type BaseFieldProps,
  type SelectOption,
  type SelectOptionGroup
} from "./fieldTypes";

const NONE = "__none__";

type Props<T extends FieldValues> = BaseFieldProps<T> & {
  options?: ReadonlyArray<string | SelectOption>;
  groups?: ReadonlyArray<SelectOptionGroup>;
  placeholder?: string;
  /** Adds a "None" item that clears the value. */
  clearable?: boolean;
};

/** Radix select bound to a string field; `""` means nothing chosen. */
export function SelectField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  options,
  groups,
  placeholder = "Select",
  clearable = false
}: Props<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  const flat = groups
    ? groups.flatMap((g) => g.options)
    : toOptions(options ?? []);

  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? "");
        // Radix treats "" as "no selection"; only pass known values.
        const selected = flat.some((o) => o.value === value)
          ? value
          : undefined;
        return (
          <FieldShell
            id={id}
            label={label}
            required={required}
            description={description}
            error={fieldState.error}
            disabled={disabled}
            className={className}
          >
            <Select
              value={selected ?? ""}
              onValueChange={(v) => field.onChange(v === NONE ? "" : v)}
              disabled={disabled}
              name={field.name}
            >
              <SelectTrigger
                id={id}
                ref={field.ref}
                className="w-full"
                aria-invalid={fieldState.invalid || undefined}
                onBlur={field.onBlur}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-72">
                {clearable ? (
                  <SelectItem value={NONE} className="text-muted-foreground">
                    None
                  </SelectItem>
                ) : null}
                {groups
                  ? groups.map((g) => (
                      <SelectGroup key={g.label}>
                        <SelectLabel>{g.label}</SelectLabel>
                        {g.options.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))
                  : flat.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </FieldShell>
        );
      }}
    />
  );
}
